import { Head, Link, router, useForm } from "@inertiajs/react"
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react"
import { useEffect, useState } from "react"
import { AppLayout } from "../../components/layout/AppLayout"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"

type CompanyRef = {
  id: number
  name: string
  cnpj: string
}

type Client = {
  id: number
  cliente: string
  company: CompanyRef | null
  codigo_sap?: string | null
  cr?: string | null
  fiscal_settings_count: number
  cadastro_minimo_omie?: boolean
  omie_n_cod_cli?: number | null
  cpf_cnpj_tomador: string
  inscricao_municipal_tomador?: string | null
  inscricao_estadual_tomador?: string | null
  razao_social_tomador: string
  tipo_endereco_tomador?: string | null
  endereco_tomador: string
  numero_endereco_tomador: string
  complemento_endereco_tomador?: string | null
  bairro_tomador: string
  cidade_tomador: string
  uf_tomador: string
  cep_tomador: string
  exterior?: boolean
}

type Filters = {
  q: string
  empresa_id: string
  page: number
  per_page: number
}

type PaginationMeta = {
  current_page: number
  per_page: number
  total_pages: number
  total_count: number
}

type OmieSyncCompany = {
  id: number
  name: string
}

type ClientesIndexProps = {
  clients: Client[]
  companies: CompanyRef[]
  omie_sync_companies: OmieSyncCompany[]
  filters: Filters
  pagination: PaginationMeta
}

function visitIndex(params: {
  q?: string
  empresa_id?: string
  page?: number
  per_page?: number
}, filters: Filters) {
  router.get(
    "/clientes",
    {
      q: params.q ?? filters.q,
      empresa_id: params.empresa_id ?? filters.empresa_id,
      page: params.page ?? filters.page,
      per_page: params.per_page ?? filters.per_page
    },
    { preserveState: true, preserveScroll: true, replace: true }
  )
}

export default function ClientesIndex({ clients, companies, omie_sync_companies, filters, pagination }: ClientesIndexProps) {
  const [qInput, setQInput] = useState(filters.q)
  const syncOmieForm = useForm({ company_id: "" })

  const omieCompanyIds = new Set(omie_sync_companies.map((c) => c.id))
  const empresaFilterId =
    filters.empresa_id && filters.empresa_id !== "none" ? Number(filters.empresa_id) : 0
  const canSyncLastOmiePage = empresaFilterId > 0 && omieCompanyIds.has(empresaFilterId)

  useEffect(() => {
    setQInput(filters.q)
  }, [filters.q])

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (qInput === filters.q) return
      visitIndex({ q: qInput, page: 1 }, filters)
    }, 400)
    return () => window.clearTimeout(t)
  }, [qInput])

  const from = pagination.total_count === 0 ? 0 : (pagination.current_page - 1) * pagination.per_page + 1
  const to = Math.min(pagination.current_page * pagination.per_page, pagination.total_count)

  return (
    <>
      <Head title="Clientes" />
      <AppLayout>
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-white">Clientes</h1>
            <p className="text-slate-400">Gerencie os clientes cadastrados no sistema.</p>
          </div>

          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between">
            <div className="grid w-full gap-4 sm:grid-cols-2 lg:max-w-4xl lg:grid-cols-3">
              <div className="space-y-2 sm:col-span-2 lg:col-span-1">
                <Label htmlFor="clientes-q" className="text-slate-300">
                  Busca
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="clientes-q"
                    placeholder="Nome, razão social, CPF/CNPJ, SAP ou CR…"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    className="border-slate-700 bg-slate-900 pl-9 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="clientes-empresa" className="text-slate-300">
                  Empresa vinculada
                </Label>
                <select
                  id="clientes-empresa"
                  value={filters.empresa_id}
                  onChange={(e) => visitIndex({ empresa_id: e.target.value, page: 1 }, filters)}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                >
                  <option value="">Todas as empresas</option>
                  <option value="none">Sem empresa vinculada</option>
                  {companies.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="clientes-per" className="text-slate-300">
                  Por página
                </Label>
                <select
                  id="clientes-per"
                  value={String(filters.per_page)}
                  onChange={(e) => visitIndex({ per_page: Number(e.target.value), page: 1 }, filters)}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <p className="text-sm text-slate-400">
                {pagination.total_count === 0
                  ? "Nenhum cliente nesta página"
                  : `${from}–${to} de ${pagination.total_count} cliente(s)`}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="border-slate-600 bg-slate-900 text-slate-100 hover:bg-slate-800"
                  disabled={!canSyncLastOmiePage || syncOmieForm.processing}
                  title={
                    canSyncLastOmiePage
                      ? "Importa só a última página de clientes da Omie para esta empresa"
                      : "Filtre por uma empresa com Omie (lista «Empresa vinculada») para habilitar"
                  }
                  onClick={() => {
                    if (!canSyncLastOmiePage) return
                    syncOmieForm.setData("company_id", String(empresaFilterId))
                    syncOmieForm.post("/clientes/sincronizar-omie-ultima-pagina")
                  }}
                >
                  {syncOmieForm.processing ? "Sincronizando Omie…" : "Última página Omie"}
                </Button>
                <Button asChild>
                  <Link href="/clientes/novo">
                    <Plus className="mr-2 h-4 w-4" />
                    Novo Cliente
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-800 bg-slate-900/70 text-slate-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Empresa</th>
                  <th className="px-4 py-3 font-medium">CPF/CNPJ</th>
                  <th className="px-4 py-3 font-medium">Razão Social</th>
                  <th className="px-4 py-3 font-medium">SAP / CR</th>
                  <th className="px-4 py-3 font-medium">Cidade/UF</th>
                  <th className="px-4 py-3 text-center font-medium">Configs fiscais</th>
                  <th className="px-4 py-3 text-center font-medium">Omie / pendências</th>
                  <th className="px-4 py-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-400" colSpan={9}>
                      Nenhum cliente encontrado com estes filtros.
                    </td>
                  </tr>
                ) : (
                  clients.map((client) => (
                    <tr key={client.id} className="border-b border-slate-800/80 last:border-b-0">
                      <td className="px-4 py-3 font-medium text-slate-100">{client.cliente}</td>
                      <td className="px-4 py-3 text-slate-200">
                        {client.company ? (
                          <div>
                            <div className="font-medium text-slate-100">{client.company.name}</div>
                            <div className="font-mono text-xs text-slate-500">{client.company.cnpj}</div>
                          </div>
                        ) : (
                          <span className="text-slate-500">Sem empresa</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-300">{client.cpf_cnpj_tomador}</td>
                      <td className="px-4 py-3 text-slate-200">{client.razao_social_tomador}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-300">
                        {[client.codigo_sap, client.cr].filter(Boolean).length > 0
                          ? `${client.codigo_sap ?? "—"} / ${client.cr ?? "—"}`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-200">
                        {[client.cidade_tomador, client.uf_tomador].filter(Boolean).join("/") || "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-200">{client.fiscal_settings_count}</td>
                      <td className="px-4 py-3 text-center text-xs text-slate-300">
                        <div className="flex flex-col items-center gap-1">
                          {client.omie_n_cod_cli != null && client.omie_n_cod_cli > 0 ? (
                            <span className="font-mono text-emerald-400/90">nCodCli {client.omie_n_cod_cli}</span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                          {client.exterior && (
                            <span className="rounded bg-sky-950/60 px-1.5 py-0.5 text-sky-200/95">Exterior</span>
                          )}
                          {client.cadastro_minimo_omie && (
                            <span className="rounded bg-amber-950/60 px-1.5 py-0.5 text-amber-200/95">Endereço incompleto</span>
                          )}
                          {client.fiscal_settings_count === 0 && (
                            <span className="rounded bg-rose-950/50 px-1.5 py-0.5 text-rose-200/90">Sem config. fiscal</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button asChild variant="outline" className="border-slate-700 bg-transparent">
                            <Link href={`/clientes/${client.id}/configuracoes-fiscais`}>Config. fiscal</Link>
                          </Button>
                          <Button asChild variant="outline" className="border-slate-700 bg-transparent">
                            <Link href={`/clientes/${client.id}/editar`}>Editar</Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.total_pages > 1 && (
            <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
              <p className="text-sm text-slate-500">
                Página {pagination.current_page} de {pagination.total_pages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-slate-700 bg-transparent"
                  disabled={pagination.current_page <= 1}
                  onClick={() => visitIndex({ page: pagination.current_page - 1 }, filters)}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Anterior
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-slate-700 bg-transparent"
                  disabled={pagination.current_page >= pagination.total_pages}
                  onClick={() => visitIndex({ page: pagination.current_page + 1 }, filters)}
                >
                  Próxima
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </AppLayout>
    </>
  )
}
